// src/store/api/apiSlice.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { notification } from "antd";
import { getEnvironmentConfig } from "../../config/environment";
import { logout } from "../auth/authSlice";
import { extractAssignmentsFromPayload } from "../../utils/interpreterAssignment";

const showNotification = (type, message, description) => {
  notification[type]({
    message,
    description,
    placement: "topRight",
    duration: 3,
  });
};
const { baseURL } = getEnvironmentConfig();

const isPublicSurveyPath = () => {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "";
  return (
    path.startsWith("/form-submissions") ||
    path.startsWith("/campaign-submissions")
  );
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: baseURL,
  prepareHeaders: (headers, { getState }) => {
    // Panel/campaign survey links are public. A leftover admin/org session in
    // the same browser must not be sent — cookies/JWTs can 401 or mark the
    // panel token invalid and show "survey unavailable".
    if (!isPublicSurveyPath()) {
      const token = getState()?.auth?.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
    return headers;
  },
});

const baseQueryWithAuth = (args, api, extraOptions) => {
  const credentials = isPublicSurveyPath() ? "omit" : "include";
  const requestArgs =
    typeof args === "string"
      ? { url: args, credentials }
      : { ...args, credentials };

  return rawBaseQuery(requestArgs, api, extraOptions);
};

const baseQueryWithAuthAndReauth = async (args, api, extraOptions) => {
  const result = await baseQueryWithAuth(args, api, extraOptions);

  // Check if response indicates an auth error
  if (result.error) {
    const status = result.error.status;
    const errorData = result.error.data;

    // Check if it's a 404 with "invalid access token" message for organization users
    if (
      status === 404 &&
      errorData?.message === "invalid access token" &&
      errorData?.error === "Not Found"
    ) {
      // Get current user role from state
      const state = api.getState();
      const userRole = state.auth.role;

      // Suppress this error for organization users
      if (userRole === "organization") {
        // Return a successful result to prevent error notifications
        return {
          data: null,
          meta: {
            request: args,
            response: result.meta?.response,
          },
        };
      }
    }

    if (status === 401 || status === 403) {
      if (isPublicSurveyPath()) {
        return result;
      }

      // Show notification
      notification.error({
        message: "Session Expired",
        description: "Your session has expired. Please login again.",
        duration: 3,
      });

      // Dispatch logout action
      api.dispatch(logout());

      // Redirect to login page
      window.location.href = "/auth/login";
    }
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuthAndReauth,

  tagTypes: [
    "Notifications",
    "Questions",
    "QuestionTemplates",
    "ArchivedForm",
    "User",
    "Organization",
    "Interpreter",
    "Campaign",
    "Form",
    "Question",
    "VideoCatalog",
    "FormResponses",
    "AssignedVideos",
    "CampaignStats",
    "DashboardStats",
    "DashboardYearlyStats",
    "FormTemplates",
    "FormStats",
    "InterpreterStats",
    "PanelUser",
    "PanelUserStats",
    "PanelUserRespondents",
    "OrganizationStats",
    "OrganizationForm",
    "ArchivedOrganizationForm",
    "FormUsers",
    "Assignment",
    "AssignmentStats",
    "Support",
    "Contacts",
    "Demos"
  ],
  endpoints: (builder) => ({
    // Auth endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
      transformResponse: (response) => {
        return response; // Return the raw response, we'll handle it in the component
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Login Failed",
            description:
              error.error?.data?.message ||
              "Something went wrong, please try again",
          });
        }
      },
    }),
    addAssignmentLog: builder.mutation({
      query: ({ assignmentId, logData }) => ({
        url: `form/assignment/${assignmentId}/logs`,
        method: "POST",
        body: logData,
      }),
      invalidatesTags: ["Assignment"],
    }),
    adminLogin: builder.mutation({
      query: (credentials) => ({
        url: "/admin/validate",
        method: "POST",
        body: credentials,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Login Failed",
            description:
              error.error?.data?.message ||
              "Something went wrong, please try again",
          });
        }
      },
    }),

    // OTP Verification endpoints
    verifyOTP: builder.mutation({
      query: (data) => ({
        url: "/auth/verify-otp",
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "OTP Verification Failed",
            description:
              error.error?.data?.message ||
              "Failed to verify OTP. Please try again.",
          });
        }
      },
    }),

    /** Admin login uses `/admin/validate`; some backends register trusted device only here. */
    adminVerifyOTP: builder.mutation({
      query: (data) => ({
        url: "/admin/verify-otp",
        method: "POST",
        body: data,
      }),
    }),

    resendOTP: builder.mutation({
      query: (data) => ({
        url: "/auth/resend-otp",
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Resend OTP Failed",
            description:
              error.error?.data?.message ||
              "Failed to resend OTP. Please try again.",
          });
        }
      },
    }),

    // Organization endpoints
    getOrganizations: builder.query({
      query: (params) => ({
        url: "organization/all",
        params,
      }),
      providesTags: ["Organization"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh organization stats when organizations are fetched
          dispatch(apiSlice.util.invalidateTags(["OrganizationStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    createOrganization: builder.mutation({
      query: (data) => ({
        url: "organization",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Organization"],
    }),
    updateOrganization: builder.mutation({
      query: ({ id, data }) => ({
        url: `organization/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Organization"],
    }),
    deleteOrganization: builder.mutation({
      query: (id) => ({
        url: `organization/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Organization", "Stats"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Organization deleted successfully!",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to delete organization",
          });
        }
      },
    }),
    verifyResetCode: builder.mutation({
      query: (data) => ({
        url: "/auth/reset-password-verify-code",
        method: "POST",
        body: data,
      }),
    }),

    // Reset password with code endpoint
    resetPassword: builder.mutation({
      query: (data) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: data,
      }),
    }),
    resetPasswordSendLink: builder.mutation({
      query: (data) => ({
        url: "auth/reset-password-send-link",
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error("Failed to send reset link:", error);
          notification.error({
            message: error.error.data.message,
          });
        }
      },
    }),

    // Interpreter endpoints
    getInterpreters: builder.query({
      query: (params) => ({
        url: "interpreter/all",
        params,
      }),
      providesTags: ["Interpreter"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh interpreter stats when interpreters are fetched
          dispatch(apiSlice.util.invalidateTags(["InterpreterStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    getInterpreterStats: builder.query({
      query: () => "interpreter/stats",
      providesTags: ["InterpreterStats"],
    }),
    getAllInterpreters: builder.query({
      query: () => ({
        url: "form/interpreter/all",
      }),
      providesTags: ["Interpreter"],
    }),

    deleteInterpreter: builder.mutation({
      query: (id) => ({
        url: `interpreter/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Interpreter", "InterpreterStats"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Interpreter deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete interpreter",
          });
        }
      },
    }),

    updateInterpreterStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `interpreter/${id}/status`,
        method: "PUT",
        body: { is_deactivated: status },
      }),
      invalidatesTags: ["Interpreter"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Interpreter status updated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to update interpreter status",
          });
        }
      },
    }),

    getInterpreterAssignments: builder.query({
      query: (interpreterId) => ({
        url: `form/interpreter/${interpreterId}/assignment`,
      }),
      transformResponse: (response) => ({
        data: extractAssignmentsFromPayload(response),
        stats: response?.stats ?? response?.data?.stats ?? {},
      }),
      providesTags: ["Assignment"],
    }),

    assignInterpreterToQuestion: builder.mutation({
      query: ({ questionId, interpreterId, organizationId }) => ({
        url: `form/question/${questionId}/organization/${organizationId}/assignment`,
        method: "POST",
        body: {
          total_time: "00:00:00",
          avg_time: "00:00",
          assignment: [interpreterId],
        },
      }),
      invalidatesTags: ["Assignment", "Interpreter", "AssignmentStats"],
    }),
    unassignInterpreter: builder.mutation({
      query: (assignmentId) => ({
        url: `form/assignment/${assignmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Assignment", "Interpreter"],
    }),

    getAssignmentStats: builder.query({
      query: () => "form/interpreter/assignment/stats",
      transformResponse: (response) => response.data,
      providesTags: ["AssignmentStats"],
    }),

    getAllAssignments: builder.query({
      query: (params) => {
        const queryParams = {};
        [
          "organization",
          "start_date",
          "end_date",
          "sort",
          "interpreter",
          "status",
          "search",
        ].forEach((key) => {
          if (params?.[key] != null && params[key] !== "") {
            queryParams[key] = params[key];
          }
        });

        return {
          url: "form/assignment/all",
          params: queryParams,
        };
      },
      transformResponse: (response) => {
        const payload = response?.data ?? response ?? {};

        return {
          data: extractAssignmentsFromPayload(response),
          stats: payload.stats ?? response?.stats ?? {},
        };
      },
      // Use more specific tags with IDs where possible
      providesTags: (result) => {
        const assignments = Array.isArray(result?.data) ? result.data : [];
       
        return [
          ...assignments.map((assignment) => ({
            type: "Assignment",
            id: assignment._id,
          })),
          { type: "Assignment", id: "LIST" },
        ];
      },
    }),
    updateAssignmentTime: builder.mutation({
      query: ({ assignmentId, timeData }) => ({
        url: `form/assignment/${assignmentId}/time`,
        method: "PUT",
        body: timeData,
      }),
      invalidatesTags: ["Assignment", "Interpreter"],
    }),

    createInterpreter: builder.mutation({
      query: (data) => ({
        url: "interpreter",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Interpreter", "InterpreterStats"],
    }),
    updateInterpreter: builder.mutation({
      query: ({ id, data }) => ({
        url: `interpreter/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Interpreter"],
    }),
    deleteInterpreter: builder.mutation({
      query: (id) => ({
        url: `interpreter/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Interpreter", "InterpreterStats"],
    }),
    updateInterpreterStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `interpreter/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Interpreter"],
    }),

    // Panel user endpoints
    getPanelUsers: builder.query({
      query: (params) => ({
        url: "panel-user/all",
        params,
      }),
      providesTags: ["PanelUser"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(apiSlice.util.invalidateTags(["PanelUserStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    getPanelUserStats: builder.query({
      query: () => "panel-user/stats",
      providesTags: ["PanelUserStats"],
    }),
    getPanelUserRespondents: builder.query({
      query: () => "panel-user/respondents/all",
      providesTags: ["PanelUserRespondents"],
    }),
    createPanelUser: builder.mutation({
      query: (data) => ({
        url: "panel-user",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PanelUser", "PanelUserStats"],
    }),
    createPanelUsersBulk: builder.mutation({
      query: (emails) => ({
        url: "panel-user",
        method: "POST",
        body: { emails },
      }),
      invalidatesTags: ["PanelUser", "PanelUserStats"],
    }),
    importPanelUsersCsv: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: "panel-user/import",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["PanelUser", "PanelUserStats"],
    }),
    updatePanelUser: builder.mutation({
      query: ({ id, data }) => ({
        url: `panel-user/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["PanelUser"],
    }),
    deletePanelUser: builder.mutation({
      query: (id) => ({
        url: `panel-user/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PanelUser", "PanelUserStats"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Panel user deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete panel user",
          });
        }
      },
    }),
    sendPanelUserSurveyLink: builder.mutation({
      query: (data) => ({
        url: "panel-user/send-survey-link",
        method: "POST",
        body: data,
      }),
    }),
    checkPanelUserSurveyAccess: builder.query({
      query: (token) => ({
        url: "panel-user/survey-access",
        method: "GET",
        params: { t: token },
      }),
      transformResponse: (response) => response?.data ?? response,
    }),
    retryPanelSurveyPayout: builder.mutation({
      query: ({ token }) => ({
        url: "panel-user/survey-payout/retry",
        method: "POST",
        body: { token },
      }),
    }),
    updatePanelUserStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `panel-user/${id}/status`,
        method: "PUT",
        body: { is_deactivated: status },
      }),
      invalidatesTags: ["PanelUser"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Panel user status updated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to update panel user status",
          });
        }
      },
    }),

    uploadFile: builder.mutation({
      query: (arg) => {
        if (arg instanceof FormData) {
          return {
            url: "file/create",
            method: "POST",
            body: arg,
          };
        }

        if (typeof File !== "undefined" && arg instanceof File) {
          const formData = new FormData();
          formData.append("file", arg, arg.name);
          return {
            url: "file/create",
            method: "POST",
            body: formData,
          };
        }

        if (typeof Blob !== "undefined" && arg instanceof Blob) {
          const formData = new FormData();
          formData.append(
            "file",
            arg,
            arg.name || `upload-${Date.now()}`,
          );
          return {
            url: "file/create",
            method: "POST",
            body: formData,
          };
        }

        const { file, filename } = arg || {};
        const formData = new FormData();

        if (file) {
          formData.append(
            "file",
            file,
            filename || file.name || `upload-${Date.now()}`,
          );
        }

        return {
          url: "file/create",
          method: "POST",
          body: formData,
          params: filename ? { filename } : undefined,
        };
      },
    }),

    getFile: builder.query({
      query: (key) => ({
        url: `file/file/${key}`, // key = filename or S3 key
        method: "GET",
      }),
      transformResponse: (response) => response.file, // extract the file URL
    }),

    generatePresignedUrl: builder.mutation({
      query: (data) => ({
        url: "file/presigned-url",
        method: "POST",
        body: {
          fileName: data.fileName,
          contentType: data.contentType,
          expiresIn: data.expiresIn || 300,
        },
      }),
    }),
    getOrganizationStats: builder.query({
      query: () => "organization/stats",
      transformResponse: (response) => response.data,
    }),
    getOrganizationById: builder.query({
      query: (id) => ({
        url: `organization/all`,
        params: { organization: id },
      }),
      transformResponse: (response) => {
        // Transform to get first organization and include error handling
        if (!response?.success || !response?.data?.length) {
          throw new Error("Organization not found");
        }
        return {
          success: response.success,
          statusCode: response.statusCode,
          message: response.message,
          data: response.data[0], // Get first organization
        };
      },
      // Add error handling
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.message || "Failed to fetch organization details",
          });
        }
      },
      providesTags: (result, error, id) => [{ type: "Organization", id }],
    }),

    archiveForm: builder.mutation({
      query: (id) => ({
        url: `form/${id}/archive`,
        method: "DELETE", // POST for archiving
      }),
      invalidatesTags: ["Form", "ArchivedForm"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          throw error;
        }
      },
    }),
    unarchiveForm: builder.mutation({
      query: (id) => ({
        url: `form/${id}/remove-archive`,
        method: "PUT", // PUT for unarchiving (matches backend controller)
      }),
      invalidatesTags: ["Form", "ArchivedForm"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          throw error;
        }
      },
    }),
    // Campaign endpoints
    getCampaigns: builder.query({
      query: (params) => {
        // Convert params object to proper URL params
        const queryParams = {};

        if (params && typeof params === "object") {
          // Handle organization parameter specifically
          if (params.organization) {
            queryParams.organization = params.organization;
          }

          // Handle any other parameters you might need
          if (params.status) {
            queryParams.status = params.status;
          }
        }

        return {
          url: "campaign/all",
          params: queryParams,
        };
      },
      providesTags: ["Campaign"],
      keepUnusedDataFor: 300,
    }),
    getCampaignsByOrganization: builder.query({
      query: (organizationId) => ({
        url: `campaign/organization/${organizationId}`,
      }),
      providesTags: ["Campaign"],
      keepUnusedDataFor: 300,
    }),
    createCampaign: builder.mutation({
      query: (data) => ({
        url: "campaigns/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Campaign"],
    }),

    // Form endpoints
    getForms: builder.query({
      query: (params) => ({
        url: "form/all",
        params: {
          ...params,
          archived: window.location.pathname.includes("/archived"),
        },
      }),
      providesTags: (result, error, arg) => {
        const isArchived = window.location.pathname.includes("/archived");
        return [
          {
            type: isArchived ? "ArchivedForm" : "Form",
          },
        ];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh form stats when forms are fetched
          dispatch(apiSlice.util.invalidateTags(["FormStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    getOrganizationForms: builder.query({
      query: (params) => ({
        url: "form/organization/all",
        params: {
          ...params,
          archived: window.location.pathname.includes("/archived"),
        },
      }),
      providesTags: (result, error, arg) => {
        const isArchived = window.location.pathname.includes("/archived");
        return [
          {
            type: isArchived ? "ArchivedOrganizationForm" : "OrganizationForm",
          },
        ];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh form stats when organization forms are fetched
          dispatch(apiSlice.util.invalidateTags(["FormStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    getAllFormsForOrganization: builder.query({
      query: (params) => ({
        url: "form/organization/all",
        params: {
          ...params,
          archived: window.location.pathname.includes("/archived"),
        },
      }),
      providesTags: (result, error, arg) => {
        const isArchived = window.location.pathname.includes("/archived");
        return [
          {
            type: isArchived ? "ArchivedOrganizationForm" : "OrganizationForm",
          },
        ];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh form stats when organization forms are fetched
          dispatch(apiSlice.util.invalidateTags(["FormStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    getFormsByOrganization: builder.query({
      query: (organizationId) => ({
        url: "form/all",
        params: {
          organization: organizationId,
        },
      }),
      providesTags: ["Form"],

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh form stats when organization forms are fetched
          dispatch(apiSlice.util.invalidateTags(["FormStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    getFormDetails: builder.query({
      query: (id) => `form/${id}`,
      providesTags: (result, error, id) => [{ type: "Form", id }],
    }),
    getFormStatsById: builder.query({
      query: (formId) => ({
        url: `form/${formId}/stats`,
      }),
      providesTags: ["FormStats"],
    }),
    getSubmitToOrganizationStats: builder.query({
      query: (formId) => ({
        url: `form/${formId}/submit-to-organization/stats`,
      }),
      providesTags: ["FormStats"],
    }),
    getSubmitToOrganizationStatsArray: builder.query({
      query: () => ({
        url: `form/submit-to-organization/stats/array`,
      }),
      providesTags: ["FormStats"],
    }),
    getFormStatsForExport: builder.query({
      query: (formId) => ({
        url: `form/${formId}/stats`,
      }),
      transformResponse: (response) => {
        // Transform the response to match the expected structure for export report
        const data = response?.data || response;
        return {
          ...data,
          total: data?.total_participants || data?.total || 0,
          totalResponses: data?.total_responses || data?.totalResponses || 0,
          responseRate:
            data?.response_ratio ??
            data?.response_rate ??
            data?.responseRate ??
            0,
          totalQuestions: data?.total_questions || 0,
          complete: data?.completion_ratio ?? data?.complete ?? 0,
        };
      },
      providesTags: ["FormStats"],
    }),
    createForm: builder.mutation({
      query: (data) => ({
        url: "form",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Form"],
    }),
    createFormTemplate: builder.mutation({
      query: ({ data }) => ({
        url: `form/template`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Form"],
    }),
    updateFormTemplate: builder.mutation({
      query: ({ id, data }) => ({
        url: `form/${id}/template`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Form", "FormTemplates"],
    }),
    activateForm: builder.mutation({
      query: (id) => ({
        url: `form/${id}/activate`,
        method: "PUT",
      }),
      invalidatesTags: ["Form", "FormStats"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Form activated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to activate form",
          });
        }
      },
    }),
    deactivateForm: builder.mutation({
      query: (id) => ({
        url: `form/${id}/deactivate`,
        method: "PUT",
      }),
      invalidatesTags: ["Form", "FormStats"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Form deactivated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to deactivate form",
          });
        }
      },
    }),
    updateForm: builder.mutation({
      query: ({ id, data }) => ({
        url: `form/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Form"],
    }),
    submitFormToOrganization: builder.mutation({
      query: ({ id, data }) => ({
        url: `form/${id}/submit-to-organization`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Form", "Organization"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Submitted to organization successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to submit to organization",
          });
        }
      },
    }),
    deleteForm: builder.mutation({
      query: (id) => ({
        url: `form/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Form"],
    }),
    updateFormStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `form/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Form"],
    }),
    updateDatesActivate: builder.mutation({
      query: ({ id, startDate, endDate }) => ({
        url: `form/${id}/update-dates-activate`,
        method: "PUT",
        body: {
          start_date: startDate,
          end_date: endDate,
        },
      }),
      invalidatesTags: ["Form", "FormStats"],
      async onQueryStarted(
        { id, startDate, endDate },
        { queryFulfilled, dispatch }
      ) {
        try {
          // Validate input parameters
          if (!id || !startDate || !endDate) {
            throw new Error("Missing required parameters");
          }

          // Validate date format
          const startTimestamp = parseInt(startDate);
          const endTimestamp = parseInt(endDate);

          if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
            throw new Error("Invalid date format");
          }

          if (startTimestamp >= endTimestamp) {
            throw new Error("End date must be after start date");
          }

          const result = await queryFulfilled;

          // Only show success notification if the operation was successful
          if (result.data && result.data.success) {
            notification.success({
              message: "Success",
              description:
                result.data.message ||
                "Form dates updated and activated successfully",
            });
          } else {
            throw new Error(result.data?.message || "Operation failed");
          }
        } catch (error) {
          console.error("Error in updateDatesActivate mutation:", error);

          // Handle different types of errors
          if (error.message === "Missing required parameters") {
            notification.error({
              message: "Validation Error",
              description: "Missing required form ID or date parameters",
            });
          } else if (error.message === "Invalid date format") {
            notification.error({
              message: "Validation Error",
              description:
                "Invalid date format. Please check your date selection.",
            });
          } else if (error.message === "End date must be after start date") {
            notification.error({
              message: "Validation Error",
              description: "End date must be after start date",
            });
          } else if (error.error?.status === 400) {
            notification.error({
              message: "Validation Error",
              description:
                error.error?.data?.message || "Invalid form data provided",
            });
          } else if (error.error?.status === 404) {
            notification.error({
              message: "Form Not Found",
              description:
                "The form you're trying to activate no longer exists",
            });
          } else if (error.error?.status === 500) {
            notification.error({
              message: "Server Error",
              description:
                "An error occurred on the server. Please try again later.",
            });
          } else if (
            error.error?.status === 401 ||
            error.error?.status === 403
          ) {
            notification.error({
              message: "Permission Denied",
              description: "You don't have permission to activate this form",
            });
          } else {
            // Generic error handling
            notification.error({
              message: "Activation Failed",
              description:
                error.error?.data?.message ||
                error.message ||
                "Failed to update dates and activate form",
            });
          }
        }
      },
    }),
    duplicateForm: builder.mutation({
      query: (id) => ({
        url: `form/${id}/duplicate`,
        method: "POST",
      }),
      invalidatesTags: ["Form"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Form duplicated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to duplicate form",
          });
        }
      },
    }),
    copyFormToEnvironment: builder.mutation({
      query: ({ id, title, environment, withResponses = false }) => ({
        url: `form/${id}/copy`,
        method: "POST",
        body: {
          title,
          environment,
          with_responses: Boolean(withResponses),
        },
      }),
      invalidatesTags: ["Form"],
    }),
    getFormStats: builder.query({
      query: (params) => ({
        url: "form/stats",
        params,
      }),
      transformResponse: (response) => response.data,
      providesTags: ["FormStats"],
    }),

    // Question endpoints
    getQuestions: builder.query({
      query: () => "question/get-all",
      providesTags: ["Question"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh form stats when questions are fetched (since questions are part of forms)
          dispatch(apiSlice.util.invalidateTags(["FormStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    updateQuestion: builder.mutation({
      query: ({ formId, questionId, data }) => {
        const normalizedShowQuestionIndex =
          data?.showQuestionIndex === true || data?.showQuestionIndex === "true"
            ? true
            : false;
        const normalizedData = {
          ...data,
          showQuestionIndex: normalizedShowQuestionIndex,
          number: data?.number ?? null,
        };

        return {
          url: `form/${formId}/question/${questionId}`,
          method: "PUT",
          body: normalizedData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        "Question",
        "Questions",
        { type: "Question", id: arg.questionId },
        { type: "Form", id: arg.formId },
      ],
    }),
    getFormResponses: builder.query({
      query: (formId) => ({
        url: `form/${formId}/response`,
      }),
      providesTags: ["FormResponses"],
    }),
    getFormResponsesForOrganization: builder.query({
      query: (formId) => ({
        url: `form/${formId}/response/organization`,
      }),
      providesTags: ["FormResponses"],
    }),
    getFormQuestions: builder.query({
      query: (formId) => ({
        url: `form/${formId}/question`,
      }),
      providesTags: ["Questions"],
    }),
    getFormQuestionsForSubmittedOrganization: builder.query({
      query: (formId) => ({
        url: `form/${formId}/question/submitted-organization`,
      }),
      providesTags: ["Questions"],
    }),
    getVideoResponses: builder.query({
      query: (params) => ({
        url: "form/response/file",
        params: {
          sort: params?.sort || "desc",
          organization: params?.organization,
          start_date: params?.start_date,
          end_date: params?.end_date,
          campaign: params?.campaign,
          form: params?.form,
          ...(params?.status ? { status: params.status } : {}),
        },
      }),
      providesTags: ["FormResponses"],
    }),
    createAssignments: builder.mutation({
      query: (data) => ({
        url: "form/assignment/multiple",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "Assignment",
        "AssignedVideos",
        "FormResponses",
        "Interpreter",
        "AssignmentStats",
      ],
    }),

    getAssignedVideos: builder.query({
      query: (interpreterId) => ({
        url: `interpreter/${interpreterId}/videos`,
      }),
      providesTags: ["AssignedVideos"],
    }),

    assignVideo: builder.mutation({
      query: ({ interpreterId, videoIds }) => ({
        url: `interpreter/${interpreterId}/assign-videos`,
        method: "POST",
        body: { videoIds },
      }),
      invalidatesTags: ["AssignedVideos"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to assign videos",
          });
        }
      },
    }),

    createQuestion: builder.mutation({
      query: ({ formId, data }) => {
        const normalizedShowQuestionIndex =
          data?.showQuestionIndex === true || data?.showQuestionIndex === "true"
            ? true
            : false;
        const normalizedData = {
          ...data,
          showQuestionIndex: normalizedShowQuestionIndex,
          number: data?.number ?? null,
        };

        return {
          url: `form/${formId}/question`,
          method: "POST",
          body: normalizedData,
        };
      },
      invalidatesTags: ["Questions"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Question added successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.error?.data?.message || "Failed to add question",
          });
        }
      },
    }),

    deleteQuestion: builder.mutation({
      query: ({ formId, questionId }) => ({
        url: `form/${formId}/question/${questionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Questions"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Question deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to delete question",
          });
        }
      },
    }),
    createResponse: builder.mutation({
      query: (data) => {
        const { t, ...body } = data;
        const path = `form/${body.form}/user/${body.user}/question/${body.question}/response`;
        const url = t ? `${path}?t=${encodeURIComponent(t)}` : path;

        return {
          url,
          method: "POST",
          body,
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          const apiMessage =
            error?.error?.data?.message ||
            error?.error?.data?.payout?.payoutError ||
            error?.error?.data?.data?.payout?.payoutError ||
            "Failed to save response";

          notification.error({
            message: "Error",
            description: String(apiMessage),
            duration: 10,
            placement: "topRight",
            style: { whiteSpace: "pre-wrap", maxWidth: 440 },
          });
        }
      },
    }),
    createUserResponse: builder.mutation({
      query: (data) => ({
        url: `auth/register`, // Based on the POST URL in screenshot
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to save response",
          });
        }
      },
    }),
    createcontactResponse: builder.mutation({
      query: (data) => ({
        url: "/contact-us",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Contacts"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.error?.data?.message || "Failed to submit form",
          });
        }
      },
    }),

    // Demo request endpoint
    createDemoRequest: builder.mutation({
      query: (data) => ({
        url: "/demo",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Demos"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to submit demo request",
          });
        }
      },
    }),

    updateResponse: builder.mutation({
      query: ({ id, data }) => ({
        url: `form/response/${id}`, // Based on the PUT URL in screenshot
        method: "PUT",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to update response",
          });
        }
      },
    }),
    getQuestionById: builder.query({
      query: (questionId) => ({
        url: `form/question/${questionId}`,
      }),
      providesTags: (result, error, id) => [{ type: "Question", id }],
    }),

    getQuestionResponses: builder.query({
      query: (questionId) => ({
        url: `form/question/${questionId}/response`,
      }),
      providesTags: ["QuestionResponses"],
    }),

    getCampaigns: builder.query({
      query: () => "campaign/all",
      providesTags: ["Campaign"],
      // Cache for 5 minutes to reduce API calls
      keepUnusedDataFor: 300,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh campaign stats when campaigns are fetched
          dispatch(apiSlice.util.invalidateTags(["CampaignStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),
    addFormToCampaign: builder.mutation({
      query: ({ campaignId, formId }) => ({
        url: `campaign/${campaignId}/form`,
        method: "POST",
        body: { formId },
      }),
      invalidatesTags: ["Campaign"],
    }),

    removeFormFromCampaign: builder.mutation({
      query: ({ campaignId, formId }) => ({
        url: `campaign/${campaignId}/form/${formId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Campaign"],
    }),

    getCampaignStats: builder.query({
      query: () => "campaign/stats",
      providesTags: ["CampaignStats"],
      // Cache for 1 minute
      keepUnusedDataFor: 60,
    }),

    getCampaignById: builder.query({
      query: (id) => `campaign/${id}`,
      providesTags: (result, error, id) => [{ type: "Campaign", id }],
    }),

    createCampaign: builder.mutation({
      query: (data) => ({
        url: "campaign",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Campaign", "CampaignStats"],
      // Optimistic update for instant UI feedback
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const response = await queryFulfilled;
          // Update cache immediately
          dispatch(
            apiSlice.util.updateQueryData(
              "getCampaigns",
              undefined,
              (draft) => {
                draft.data.push(response.data);
              }
            )
          );
        } catch {}
      },
    }),

    updateCampaign: builder.mutation({
      query: ({ id, data }) => ({
        url: `campaign/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Campaign", "CampaignStats"],
      // Optimistic update
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          dispatch(
            apiSlice.util.updateQueryData(
              "getCampaigns",
              undefined,
              (draft) => {
                const index = draft.data.findIndex((item) => item._id === id);
                if (index !== -1) {
                  draft.data[index] = response.data;
                }
              }
            )
          );
        } catch {}
      },
    }),

    deleteCampaign: builder.mutation({
      query: (id) => ({
        url: `campaign/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Campaign", "CampaignStats"],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            apiSlice.util.updateQueryData(
              "getCampaigns",
              undefined,
              (draft) => {
                draft.data = draft.data.filter((item) => item._id !== id);
              }
            )
          );
        } catch {}
      },
    }),

    updateAssignmentStatus: builder.mutation({
      query: ({
        assignmentId,
        status,
        translation,
        assignment,
        isSecondChecked,
        question,
        organization,
        response,
        form,
      }) => ({
        url: `form/assignment/${assignmentId}`,
        method: "PUT",
        body: {
          status,
          assignment: assignment ? assignment : [],
          isSecondChecked: isSecondChecked,
          ...(translation != null ? { translation } : {}),
          ...(question ? { question } : {}),
          ...(organization ? { organization } : {}),
          ...(response ? { response } : {}),
          ...(form ? { form } : {}),
        },
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "Assignment", id: assignmentId },
        { type: "Assignment", id: "LIST" },
        "Assignment",
        "AssignmentStats",
        "Interpreter",
      ],
    }),

    // updateAssignmentTranslation: builder.mutation({
    //   query: ({ assignmentId, translationData }) => ({
    //     url: `form/assignment/${assignmentId}/translation`,
    //     method: 'POST',
    //     body: translationData
    //   }),
    //   invalidatesTags: ['Assignment'],
    //   async onQueryStarted(_, { queryFulfilled }) {
    //     try {
    //       await queryFulfilled;
    //       notification.success({
    //         message: 'Success',
    //         description: 'Translation added successfully'
    //       });
    //     } catch (error) {
    //       notification.error({
    //         message: 'Error',
    //         description: error.error?.data?.message || 'Failed to update translation'
    //       });
    //     }
    //   }
    // }),
    updateAssignmentTranslation: builder.mutation({
      query: ({ assignmentId, translationId, translationData }) => {
        // If translationId exists, it's an update to existing translation
        if (translationId) {
          return {
            url: `form/assignment/${assignmentId}/translation/${translationId}`,
            method: "PUT",
            body: translationData,
          };
        }
        // Otherwise it's a new translation
        return {
          url: `form/assignment/${assignmentId}/translation`,
          method: "POST",
          body: translationData,
        };
      },
      invalidatesTags: ["Assignment", "AssignmentStats"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Translation updated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to update translation",
          });
        }
      },
    }),
    // Landing page

    getDemoRequests: builder.query({
      query: (params) => ({
        // url: '/demo',
        url: params?.archived ? `demo/archived` : "demo",

        method: "GET",
        // params: {
        //   archived: params?.archived
        // }
      }),
      providesTags: ["Demos"],
    }),
    archiveDemo: builder.mutation({
      query: (id) => ({
        url: `/demo/${id}/archive`,
        method: "DELETE",
      }),
      invalidatesTags: ["Demos"],
    }),
    unarchiveDemo: builder.mutation({
      query: (id) => ({
        url: `/demo/${id}/remove-archive`,
        method: "POST",
      }),
      invalidatesTags: ["Demos"],
    }),
    getContacts: builder.query({
      query: (params) => ({
        url: params?.archived ? `contact-us/archived` : "contact-us",

        method: "GET",
      }),
      providesTags: ["Contacts"],
    }),
    archiveContact: builder.mutation({
      query: (id) => ({
        url: `/contact-us/${id}/archive`,
        method: "DELETE",
      }),
      invalidatesTags: ["Contacts"],
    }),
    unarchiveContact: builder.mutation({
      query: (id) => ({
        url: `/contact-us/${id}/remove-archive`,
        method: "POST",
      }),
      invalidatesTags: ["Contacts"],
    }),
    deleteContact: builder.mutation({
      query: (id) => ({
        url: `/contact-us/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Contacts"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Contact deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to delete contact",
          });
        }
      },
    }),
    createSupport: builder.mutation({
      query: (data) => ({
        url: "/support",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Support"],
    }),
    getSupportTickets: builder.query({
      query: (params) => ({
        url: params?.archived ? `support/archived` : "support",
        method: "GET",
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response) ? response : response?.data || [],
      }),
      providesTags: ["Support"],
    }),
    updateSupportStatus: builder.mutation({
      query: ({ id, status, resolution_note }) => ({
        url: `/support/${id}/status`,
        method: "POST",
        body: {
          status,
          ...(resolution_note ? { resolution_note } : {}),
        },
      }),
      invalidatesTags: ["Support"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Status updated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to update status",
          });
        }
      },
    }),    
    archiveSupport: builder.mutation({
      query: (id) => ({
        url: `/support/${id}/archive`,
        method: "DELETE",
      }),
      invalidatesTags: ["Support"],
    }),
    unarchiveSupport: builder.mutation({
      query: (id) => ({
        url: `/support/${id}/remove-archive`,
        method: "POST",
      }),
      invalidatesTags: ["Support"],
    }),
    deleteSupport: builder.mutation({
      query: (id) => ({
        url: `/support/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Support"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Support ticket deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to delete support ticket",
          });
        }
      },
    }),
    getDashboardStats: builder.query({
      query: (params) => {
        const queryParams = {};

        if (params) {
          Object.keys(params).forEach((key) => {
            // Handle indexed parameters for organization, campaign, and form
            if (
              key.match(/^organization\[\d+\]$/) ||
              key.match(/^campaign\[\d+\]$/) ||
              key.match(/^form\[\d+\]$/)
            ) {
              queryParams[key] = params[key];
            }
            // Handle other parameters
            else if (params[key] !== null && params[key] !== undefined) {
              queryParams[key] = params[key];
            }
          });
        }

        return {
          url: "form/dashboard/stats",
          params: queryParams,
        };
      },
      providesTags: ["DashboardStats"],
    }),

    // src/store/api/apiSlice.js - Add these endpoints

    // Add to existing endpoints object
    getAdminNotifications: builder.query({
      query: () => ({
        url: "notification/admin",
      }),
      transformResponse: (response) => {
        // Ensure response has a consistent structure
        return {
          data: Array.isArray(response) ? response : response?.data || [],
        };
      },
      providesTags: ["Notifications"],
    }),

    getOrganizationNotifications: builder.query({
      query: (organizationId) => ({
        url: `notification/organization/${organizationId}`,
      }),
      transformResponse: (response) => {
        // Ensure response has a consistent structure
        return {
          data: Array.isArray(response) ? response : response?.data || [],
        };
      },
      providesTags: ["Notifications"],
    }),

    getInterpreterNotifications: builder.query({
      query: (interpreterId) => ({
        url: `notification/interpreter/${interpreterId}`,
      }),
      transformResponse: (response) => {
        // Ensure response has a consistent structure
        return {
          data: Array.isArray(response) ? response : response?.data || [],
        };
      },
      providesTags: ["Notifications"],
    }),
    deleteNotifications: builder.mutation({
      query: (notificationIds) => ({
        url: "notification",
        method: "DELETE",
        params: {
          "notifications_list[0]": notificationIds,
        },
      }),
      invalidatesTags: ["Notifications"],
    }),

    deleteDemo: builder.mutation({
      query: (id) => ({
        url: `/demo/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Demos"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Demo request deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to delete demo request",
          });
        }
      },
    }),

    getMyProfile: builder.query({
      query: (role) => ({
        url: role === "admin" ? "admin/profile" : "user/profile",
      }),
      providesTags: ["User"],
    }),

    updateUserProfile: builder.mutation({
      query: (data) => {
        const isFormData = data instanceof FormData;
        const isAdmin = data.get ? data.get("isAdmin") : data.isAdmin; // Check for FormData or regular object

        return {
          url: isAdmin ? "admin/profile" : "user/profile",
          method: "PUT",
          body:
            data instanceof FormData
              ? data
              : {
                  ...data,
                  isAdmin: undefined, // Remove isAdmin from payload
                },
          headers: isFormData
            ? {}
            : {
                "Content-Type": "application/json",
              },
        };
      },
      invalidatesTags: ["User"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Profile updated successfully",
          });
        } catch (error) {
          if (error?.error?.status !== 401 && error?.error?.status !== 403) {
            notification.error({
              message: "Error",
              description:
                error.error?.data?.message || "Failed to update profile",
            });
          }
        }
      },
    }),

    updatePassword: builder.mutation({
      query: (data) => ({
        url: data.isAdmin ? "admin/password" : "user/password",
        method: "PUT",
        body: {
          current_password: data.current_password,
          new_password: data.new_password,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Password updated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to update password",
          });
        }
      },
    }),

    updateSettings: builder.mutation({
      query: (data) => ({
        url: data.isAdmin ? "admin/profile" : "user/profile",
        method: "PUT",
        body: {
          ...data,
          isAdmin: undefined, // Remove isAdmin from payload
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Settings updated successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message || "Failed to update settings",
          });
        }
      },
    }),

    getVideoCatalog: builder.query({
      query: (params) => ({
        url: "video-catalog",
        params,
      }),
      providesTags: ["VideoCatalog"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh dashboard stats when video catalog is fetched
          dispatch(apiSlice.util.invalidateTags(["DashboardStats"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),

    uploadVideo: builder.mutation({
      query: (formData) => ({
        url: "video-catalog",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["VideoCatalog"],
    }),
    updateVideo: builder.mutation({
      query: ({ id, data }) => ({
        url: `video-catalog/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["VideoCatalog"],
    }),
    getFormTemplates: builder.query({
      query: () => "form/templates",
      providesTags: ["FormTemplates"],
      transformResponse: (response) => response.data,
    }),

    getQuestionTemplates: builder.query({
      query: () => "form/question/templates",
      providesTags: ["QuestionTemplates"],
      transformResponse: (response) => response.data,
    }),
    createQuestionTemplate: builder.mutation({
      query: (data) => ({
        url: `form/question/template`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["QuestionTemplates"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({
            message: "Success",
            description: "Question template created successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.error?.data?.message ||
              "Failed to create question template",
          });
        }
      },
    }),
    getFormUsers: builder.query({
      query: (formId) => ({
        url: `form/${formId}/users`,
      }),
      providesTags: ["FormUsers"],
    }),

    deleteVideo: builder.mutation({
      query: (id) => ({
        url: `video-catalog/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VideoCatalog"],
    }),

    getDashboardYearlyStats: builder.query({
      query: (params) => {
        const queryParams = {};

        if (params) {
          Object.keys(params).forEach((key) => {
            // Handle indexed parameters for organization, campaign, and form
            if (
              key.match(/^organization\[\d+\]$/) ||
              key.match(/^campaign\[\d+\]$/) ||
              key.match(/^form\[\d+\]$/)
            ) {
              queryParams[key] = params[key];
            }
            // Handle other parameters
            else if (params[key] !== null && params[key] !== undefined) {
              queryParams[key] = params[key];
            }
          });
        }

        return {
          url: "form/dashboard/stats/yearly",
          params: queryParams,
        };
      },
      providesTags: ["DashboardYearlyStats"],
    }),
  }),
});

export const {
  // Auth endpoints
  useLoginMutation,
  useAdminLoginMutation,
  useGetMyProfileQuery,
  useGetDashboardYearlyStatsQuery,

  // Organization endpoints
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useUpdateVideoMutation,
  useDeleteVideoMutation,

  // Interpreter endpoints
  useGetInterpretersQuery,
  useGetInterpreterStatsQuery,
  useGetDashboardStatsQuery,
  useCreateInterpreterMutation,
  useUpdateInterpreterMutation,
  useDeleteInterpreterMutation,
  useUpdateInterpreterStatusMutation,

  // Panel user endpoints
  useGetPanelUsersQuery,
  useGetPanelUserStatsQuery,
  useGetPanelUserRespondentsQuery,
  useCreatePanelUserMutation,
  useCreatePanelUsersBulkMutation,
  useImportPanelUsersCsvMutation,
  useUpdatePanelUserMutation,
  useDeletePanelUserMutation,
  useSendPanelUserSurveyLinkMutation,
  useUpdatePanelUserStatusMutation,
  useRetryPanelSurveyPayoutMutation,

  // File upload endpoints
  useUploadFileMutation,
  useGetFileQuery,
  useGeneratePresignedUrlMutation,
  // Form endpoints
  useGetFormsQuery,
  useGetOrganizationFormsQuery,
  useGetAllFormsForOrganizationQuery,
  useGetFormDetailsQuery,
  useGetFormStatsQuery,
  useGetFormStatsByIdQuery,
  useGetSubmitToOrganizationStatsQuery,
  useGetSubmitToOrganizationStatsArrayQuery,

  useGetVideoCatalogQuery,
  useUploadVideoMutation,

  useGetAdminNotificationsQuery,
  useGetOrganizationNotificationsQuery,
  useGetInterpreterNotificationsQuery,
  useDeleteNotificationsMutation,
  useCreateFormMutation,
  useUpdateFormMutation,
  useSubmitFormToOrganizationMutation,
  useDeleteFormMutation,
  useUpdateFormStatusMutation,
  useUpdateDatesActivateMutation,
  useDuplicateFormMutation,
  useCopyFormToEnvironmentMutation,

  // Question endpoints
  useGetQuestionsQuery,

  useUpdateQuestionMutation,
  useGetFormQuestionsQuery,
  useGetFormQuestionsForSubmittedOrganizationQuery,
  useCreateQuestionMutation,
  useDeleteQuestionMutation,
  useCreateResponseMutation,
  useGetFormTemplatesQuery,
  useGetQuestionTemplatesQuery,
  useCreateQuestionTemplateMutation,
  useCreateUserResponseMutation,
  useArchiveFormMutation,
  useUnarchiveFormMutation,
  useGetOrganizationStatsQuery,
  useUpdateAssignmentTranslationMutation,
  useUpdateResponseMutation,
  useGetFormResponsesQuery,
  useGetFormResponsesForOrganizationQuery,
  useAddAssignmentLogMutation,
  useGetQuestionByIdQuery,
  useGetQuestionResponsesQuery,
  useUnassignInterpreterMutation,
  useUpdateAssignmentTimeMutation,
  // Campaigns
  useGetCampaignsQuery,
  useGetCampaignsByOrganizationQuery,
  useGetCampaignStatsQuery,
  useGetCampaignByIdQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useAddFormToCampaignMutation,
  useRemoveFormFromCampaignMutation,
  useGetAllInterpretersQuery,
  useGetInterpreterAssignmentsQuery,
  useAssignInterpreterToQuestionMutation,
  useGetAssignmentStatsQuery,
  useGetOrganizationByIdQuery,
  useGetAllAssignmentsQuery,
  useCreatecontactResponseMutation,
  useCreateDemoRequestMutation,
  useCreateSupportMutation,
  useGetSupportTicketsQuery,
  useUpdateSupportStatusMutation,
  useArchiveSupportMutation,
  useUnarchiveSupportMutation,
  useDeleteSupportMutation,
  useGetContactsQuery,
  useGetDemoRequestsQuery,
  useUpdateAssignmentStatusMutation,
  useArchiveContactMutation,
  useUnarchiveContactMutation,
  useArchiveDemoMutation,
  useUnarchiveDemoMutation,
  useDeleteContactMutation,
  useDeleteDemoMutation,
  useUpdateUserProfileMutation,
  useUpdatePasswordMutation,
  useUpdateSettingsMutation,
  useGetVideoResponsesQuery,
  useLazyGetVideoResponsesQuery,
  useGetAssignedVideosQuery,
  useAssignVideoMutation,
  useVerifyResetCodeMutation,
  useCreateAssignmentsMutation,
  useGetFormUsersQuery,
  useResetPasswordSendLinkMutation,
  useResetPasswordMutation,

  // OTP Verification mutations
  useVerifyOTPMutation,
  useAdminVerifyOTPMutation,
  useResendOTPMutation,

  useCreateFormTemplateMutation,
  useUpdateFormTemplateMutation,
  useActivateFormMutation,
  useDeactivateFormMutation,
  useGetFormStatsForExportQuery,
  useCheckPanelUserSurveyAccessQuery,
} = apiSlice;
